import { useEffect, useRef, useState } from 'react';
import { Button } from '@shopify/polaris';
import useMainStore from '../store/useMainStore';
import { useShallow } from 'zustand/shallow';
import { useNavigate } from 'react-router-dom';
import FixLoading from '@/pos/layout/FixLoading.tsx';

const SignatureCapture = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [context, setContext] = useState<CanvasRenderingContext2D | null>(null);
    const router = useNavigate();

    const { setCurrentImage } = useMainStore(
        useShallow((state) => ({
            setCurrentImage: state.setCurrentImage,
        })),
    );

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Set canvas size
        canvas.width = canvas.offsetWidth;
        canvas.height = canvas.offsetHeight;

        // Configure drawing style
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 2;

        setContext(ctx);
    }, []);

    const getCoordinates = (event: MouseEvent | TouchEvent) => {
        const canvas = canvasRef.current;
        if (!canvas) return { x: 0, y: 0 };

        const rect = canvas.getBoundingClientRect();

        if ('touches' in event) {
            // Touch event
            const touch = event.touches[0];
            return {
                x: touch.clientX - rect.left,
                y: touch.clientY - rect.top,
            };
        } else {
            // Mouse event
            return {
                x: event.clientX - rect.left,
                y: event.clientY - rect.top,
            };
        }
    };

    const startDrawing = (event: React.MouseEvent | React.TouchEvent) => {
        event.preventDefault();
        setIsDrawing(true);

        const nativeEvent = event.nativeEvent;
        const { x, y } = getCoordinates(nativeEvent as MouseEvent | TouchEvent);

        if (context) {
            context.beginPath();
            context.moveTo(x, y);
        }
    };

    const draw = (event: React.MouseEvent | React.TouchEvent) => {
        if (!isDrawing || !context) return;

        event.preventDefault();
        const nativeEvent = event.nativeEvent;
        const { x, y } = getCoordinates(nativeEvent as MouseEvent | TouchEvent);

        context.lineTo(x, y);
        context.stroke();
    };

    const stopDrawing = () => {
        setIsDrawing(false);
        if (context) {
            context.closePath();
        }
    };

    const clearCanvas = () => {
        const canvas = canvasRef.current;
        if (!canvas || !context) return;

        context.clearRect(0, 0, canvas.width, canvas.height);
    };

    const saveSignature = async () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        // Convert canvas to blob
        canvas.toBlob((blob) => {
            if (!blob) return;

            // Create a File object from the blob
            const file = new File([blob], 'signature.png', {
                type: 'image/png',
                lastModified: Date.now(),
            });

            setCurrentImage(file, 'signature');
            router('/stores');
        }, 'image/png');
    };

    return (
        <FixLoading>
            <div className="h-dvh flex flex-col p-4">
                <div className="mb-4">
                    <h2 className="text-xl font-semibold">Capture Signature</h2>
                    <p className="text-sm text-gray-600">Draw signature below</p>
                </div>

                <div className="flex-1 flex items-center justify-center">
                    <canvas
                        ref={canvasRef}
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                        onTouchStart={startDrawing}
                        onTouchMove={draw}
                        onTouchEnd={stopDrawing}
                        className="border-2 border-gray-300 rounded-lg bg-white touch-none"
                        style={{
                            width: '100%',
                            maxWidth: '600px',
                            height: '300px',
                            cursor: 'crosshair',
                        }}
                    />
                </div>

                <div className="mt-4 flex justify-end gap-3">
                    <Button onClick={clearCanvas}>Clear</Button>
                    <Button onClick={() => router('/')}>Cancel</Button>
                    <Button variant="primary" onClick={saveSignature}>
                        Save Signature
                    </Button>
                </div>
            </div>
        </FixLoading>
    );
};

export default SignatureCapture;
